import React, { useCallback, useRef } from 'react';
import { Grid } from '@mui/material';
import { CameraIcon, TrashIcon } from '@/components/icons';
import {
  GalleryWidgetRoot,
  GalleryItem,
  PlaceholderBox,
  GalleryIconBox,
} from '@/components/gallery/components';

interface CompanyGalleryWidgetProps {
  images?: string[];
  withActions?: boolean;
  compact?: boolean;
  readOnly?: boolean;
  onUploadImage?: (index: number, file: File) => void;
  onDeleteImage?: (index: number) => void;
}

const CompanyGalleryWidget: React.FC<CompanyGalleryWidgetProps> = ({
  images = [],
  withActions = false,
  compact = false,
  readOnly = false,
  onUploadImage,
  onDeleteImage,
}) => {
  const interactive = !readOnly;
  const fileInputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleCameraClick = useCallback((index: number) => {
    const input = fileInputsRef.current[index];
    if (input) {
      input.click();
    }
  }, []);

  const handleImageChange = useCallback(
    (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      const files = event.target.files;

      if (files && files[0]) {
        onUploadImage?.(index, files[0]);
      }

      event.target.value = '';
    },
    [onUploadImage]
  );

  const handleDeleteClick = useCallback(
    (index: number) => {
      onDeleteImage?.(index);
    },
    [onDeleteImage]
  );

  return (
    <GalleryWidgetRoot $compact={compact}>
      <Grid
        container
        justifyContent="center"
      >
        {Array.from({ length: 3 }).map((_, index) => {
          const src = images[index];
          const isEmpty = !src;

          return (
            <Grid
              key={index}
              item
              display="flex"
              justifyContent="center"
            >
              <input
                type="file"
                accept=".png, .jpeg, .jpg, .PNG, .JPEG, .JPG"
                style={{ display: 'none' }}
                ref={(el) => {
                  fileInputsRef.current[index] = el;
                }}
                onChange={(event) => handleImageChange(index, event)}
              />
              {isEmpty ? (
                <PlaceholderBox
                  $clickable={interactive}
                  {...(interactive && {
                    onClick: () => handleCameraClick(index),
                  })}
                >
                  Add Gallery image
                </PlaceholderBox>
              ) : (
                <GalleryItem>
                  <img
                    src={src}
                    alt={`gallery item ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                  {withActions && interactive && (
                    <>
                      <GalleryIconBox
                        sx={{ left: 1 }}
                        onClick={() => handleCameraClick(index)}
                      >
                        <CameraIcon />
                      </GalleryIconBox>
                      <GalleryIconBox
                        sx={{ right: 1 }}
                        onClick={() => handleDeleteClick(index)}
                      >
                        <TrashIcon />
                      </GalleryIconBox>
                    </>
                  )}
                </GalleryItem>
              )}
            </Grid>
          );
        })}
      </Grid>
    </GalleryWidgetRoot>
  );
};

export default CompanyGalleryWidget;

